// Loads the active org's Shopify Storefront config (publishable domain + token)
// from the server and applies it to the storefront client. This is the ONLY way
// the storefront becomes usable — there is no hardcoded global token — so every
// surface that shows products must call this first:
//   • public org storefront pages → loadStorefrontConfig(orgSlug)
//   • the in-app chat mini-shop    → loadStorefrontConfig()  (company store)
import { apiFetchJson } from '@/services/apiClient';
import { setStorefrontConfig } from '@/services/shopifyStorefront';
import { getVisitorId } from '@/utils/visitorId';

interface ShopifyConfigResponse {
  configured: boolean;
  domain?: string;
  storefrontToken?: string;
  apiVersion?: string;
}

// Dedup concurrent/identical loads (e.g. SitePage + PageRenderer mounting
// together) so we don't thrash the config or refetch needlessly.
let inFlight: { key: string; promise: Promise<boolean> } | null = null;

// The org slug of the currently-applied storefront (null = the company store,
// which has no /g/ homepage funnel). Cart-add analytics read this so a beacon
// can attribute the event to the right org without threading the slug through
// every product component.
let activeOrgSlug: string | null = null;
export function getActiveStoreOrgSlug(): string | null {
  return activeOrgSlug;
}

// Fire-and-forget add-to-cart beacon for the homepage funnel (navigator.sendBeacon,
// never blocks, swallows errors — mirrors the page-view beacon). Lives here (not in
// features/store/index.ts) so both that module AND the chat mini-shop (StoreView)
// can import it without a circular dependency between the two. Omitting orgSlug
// (the company mini-shop, which has no /g/ page) lets the server attribute the
// event to the default Sleepy Circuits org, the same way /api/storefront/
// default-config resolves the mini-shop's product catalog.
export function fireCartBeacon(orgSlug: string | null = activeOrgSlug): void {
  try {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    const body = JSON.stringify({ orgSlug: orgSlug || undefined, vid: getVisitorId() });
    navigator.sendBeacon('/api/store/cart-event', new Blob([body], { type: 'application/json' }));
  } catch {
    /* analytics is best-effort — never block a cart add */
  }
}

/**
 * Configure the storefront for the given org (by slug), or the company store
 * when no slug is passed. Returns true when a usable config was applied.
 */
export function loadStorefrontConfig(orgSlug?: string): Promise<boolean> {
  const key = orgSlug || '__default__';
  if (inFlight && inFlight.key === key) return inFlight.promise;

  const path = orgSlug
    ? `/api/pages/public/${encodeURIComponent(orgSlug)}/shopify-config`
    : '/api/storefront/default-config';

  const promise = (async () => {
    try {
      const cfg = await apiFetchJson<ShopifyConfigResponse>(path);
      if (cfg.configured && cfg.domain && cfg.storefrontToken) {
        setStorefrontConfig({ domain: cfg.domain, storefrontToken: cfg.storefrontToken, apiVersion: cfg.apiVersion || '2024-01' });
        activeOrgSlug = orgSlug || null;
        return true;
      }
      setStorefrontConfig(null);
      activeOrgSlug = null;
      return false;
    } catch {
      setStorefrontConfig(null);
      activeOrgSlug = null;
      return false;
    } finally {
      inFlight = null;
    }
  })();

  inFlight = { key, promise };
  return promise;
}
